import React, { useMemo, useState, useRef, useEffect } from 'react';
import './index.scss';

export interface IndexSortListItem {
  id: number | string;
  label: string;
  value: number | string;
}

export interface IndexSortListProps {
  data: IndexSortListItem[];
  onItemClick?: (item: IndexSortListItem) => void;
  className?: string;
  style?: React.CSSProperties;
  renderLabel?: (item: IndexSortListItem) => React.ReactNode; // 新增自定义渲染函数
}

interface GroupedData {
  [key: string]: IndexSortListItem[];
}

const IndexSortList: React.FC<IndexSortListProps> = ({
  data,
  onItemClick,
  className = '',
  style,
  renderLabel
}) => {
  const win = window as any;
  const [activeIndex, setActiveIndex] = useState<string>('');
  const [isCncharLoaded, setIsCncharLoaded] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const indexBarRef = useRef<HTMLDivElement>(null);

  // 动态加载 cnchar
  useEffect(() => {
    const url =
      'https://storage.360buyimg.com/jdx-autopilot-ota/cnchar.min.js?Expires=3897541840&AccessKey=n828WHAXD584pTvi&Signature=UonCOB31H%2FWVgvC1%2BesJmlltZs0%3D';
    const loadCnchar = async () => {
      try {
        await win.loadCDNResource(url, () => {
          setIsCncharLoaded(true);
        });
      } catch (error) {
        console.error('Failed to load cnchar:', error);
      }
    };

    loadCnchar();
  }, []);

  // 获取中文首字母并分组数据
  const groupedData = useMemo(() => {
    // 如果 cnchar 加载失败，使用 fallback：返回未分组的原始数据
    if (!isCncharLoaded || !win.cnchar || !win.cnchar.spell) {
      const grouped: GroupedData = { '全部': data || [] };
      const indexList: string[] = ['全部'];
      return { grouped, indexList };
    }

    // cnchar 加载成功，使用原有分组逻辑
    const grouped: GroupedData = {};
    const indexList: string[] = [];

    data.forEach((item) => {
      const firstLetter = win.cnchar.spell(item.label.charAt(0), 'first', 'up');
      const index = firstLetter || '#';

      if (!grouped[index]) {
        grouped[index] = [];
        indexList.push(index);
      }
      grouped[index].push(item);
    });

    // 对索引进行排序，字母在前，其他字符在后
    const sortedIndexList = indexList.sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });

    // 对每组内的数据按label排序
    sortedIndexList.forEach((index) => {
      grouped[index].sort((a, b) => a.label.localeCompare(b.label));
    });

    return { grouped, indexList: sortedIndexList };
  }, [data, isCncharLoaded]);

  // 滚动到指定索引
  const scrollToIndex = (index: string) => {
    const element = document.getElementById(`index-${index}`);
    if (element && listRef.current) {
      const offsetTop = element.offsetTop - listRef.current.offsetTop;
      listRef.current.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  // 处理索引栏触摸事件
  const handleIndexTouch = (e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    const indexBar = indexBarRef.current;
    if (!indexBar) return;

    const rect = indexBar.getBoundingClientRect();
    const y = touch.clientY - rect.top;
    const itemHeight = rect.height / groupedData.indexList.length;
    const index = Math.floor(y / itemHeight);

    if (index >= 0 && index < groupedData.indexList.length) {
      const targetIndex = groupedData.indexList[index];
      scrollToIndex(targetIndex);
    }
  };

  // 添加触摸事件监听器
  useEffect(() => {
    const indexBar = indexBarRef.current;
    if (!indexBar || groupedData.indexList.length === 0) return;

    // 添加非被动的触摸事件监听器
    indexBar.addEventListener('touchstart', handleIndexTouch, {
      passive: false
    });
    indexBar.addEventListener('touchmove', handleIndexTouch, {
      passive: false
    });

    return () => {
      indexBar.removeEventListener('touchstart', handleIndexTouch);
      indexBar.removeEventListener('touchmove', handleIndexTouch);
    };
  }, [groupedData.indexList]);

  // 监听滚动事件，更新当前激活的索引
  useEffect(() => {
    const handleScroll = () => {
      if (!listRef.current) return;

      const scrollTop = listRef.current.scrollTop;
      let currentIndex = '';

      for (const index of groupedData.indexList) {
        const element = document.getElementById(`index-${index}`);
        if (element) {
          const offsetTop = element.offsetTop - listRef.current.offsetTop;
          if (scrollTop >= offsetTop - 50) {
            currentIndex = index;
          } else {
            break;
          }
        }
      }

      setActiveIndex(currentIndex);
    };

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => listElement.removeEventListener('scroll', handleScroll);
    }
  }, [groupedData.indexList]);

  const handleItemClick = (item: IndexSortListItem) => {
    onItemClick && onItemClick(item);
  };

  return (
    <div className={`index-sort-list ${className}`} style={style}>
      <div className="index-sort-list__content" ref={listRef}>
        {groupedData.indexList.map((index) => (
          <div key={index} className="index-sort-list__group">
            <div id={`index-${index}`} className="index-sort-list__group-title">
              {index}
            </div>
            <div className="index-sort-list__group-content">
              {groupedData.grouped[index].map((item) => (
                <div
                  key={item.id}
                  className="index-sort-list__item"
                  onClick={() => handleItemClick(item)}
                >
                  <span className="index-sort-list__item-label">
                    {renderLabel ? renderLabel(item) : item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="index-sort-list__index-bar" ref={indexBarRef}>
        {groupedData.indexList.map((index) => (
          <div
            key={index}
            className={`index-sort-list__index-item ${
              activeIndex === index ? 'active' : ''
            }`}
            onClick={() => scrollToIndex(index)}
          >
            {index}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndexSortList;
